import { PixiSettings } from "../../../../pixiSettings";

/**
 * Shared title and subtitle font configuration for all paytable slides
 * These are consistent across all slide types
 */
export const getSharedTitleConfig = () => {
  return {
    baseTitleFontSize: 46, // Yellow subtitle at top of each slide
  };
};

/**
 * Symbols Slide 1 configuration (slideIndex 0)
 * Controls text, image sizing, and layout for first symbols slide (Wild & Coin)
 */
export const getSymbols1SlideTextConfig = (device, orientation) => {
  if (device === PixiSettings.DEVICE.MOBILE) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 0.9,
        descriptionFontMultiplier: 0.75,
        gapMultiplier: 0.75,
        portraitTopOffset: 0.1,
        landscapeTopOffset: 0.0,
        imageSizeMultiplier: 4.5, // Multiplier for symbol image size (relative to title font)
        columnWidthMultiplier: 0.9, // % of maxWidth for content in portrait
        portraitRowGapMultiplier: 5.0, // Gap between symbols in portrait (multiplier of subtitleFontSize)
      };
    } else {
      return {
        titleFontMultiplier: 0.75,
        descriptionFontMultiplier: 0.65
        ,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: -0.075,
        imageSizeMultiplier: 3.5,
        columnWidthMultiplier: 0.42,
        portraitRowGapMultiplier: 1.0,
      };
    }
  } else if (device === PixiSettings.DEVICE.TABLET) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 0.9,
        descriptionFontMultiplier: 0.75,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.1,
        landscapeTopOffset: 0.0,
        imageSizeMultiplier:5.0,
        columnWidthMultiplier: 0.85,
        portraitRowGapMultiplier: 2.5,
      };
    } else {
      return {
        titleFontMultiplier: 1.25,
        descriptionFontMultiplier: 1.0,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: -0.05,
        imageSizeMultiplier: 6.0,
        columnWidthMultiplier: 0.42,
        portraitRowGapMultiplier: 1.0,
      };
    }
  } else {
    // Desktop
    return {
      titleFontMultiplier: 0.7,
      descriptionFontMultiplier: 0.6,
      gapMultiplier: 1.0,
      portraitTopOffset: 0.05,
      landscapeTopOffset: 0,
      imageSizeMultiplier: 4.5,
      columnWidthMultiplier: 0.42,
      portraitRowGapMultiplier: 1.0,
    };
  }
};

/**
 * Symbols Slide 2 configuration (slideIndex 1)
 * Controls text, image sizing, and layout for second symbols slide (regular symbols paytable grid)
 */
export const getSymbols2SlideTextConfig = (device, orientation) => {
  if (device === PixiSettings.DEVICE.MOBILE) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 1.0,
        descriptionFontMultiplier: 0.75,
        gapMultiplier: 0.01,
        portraitTopOffset: 0.05,
        landscapeTopOffset: 0.0,
        imageSizeMultiplier: 2.25,
        columnWidthMultiplier: 0.85,
        portraitRowGapMultiplier: 1.0,
        gridSymbolSizeMultiplier: 1.3, // Size multiplier for symbols in grid (relative to base symbol size)
        paytableFontMultiplier: 0.5, // Paytable text size (relative to title font)
        rowGapMultiplier: 0.01, // Gap between rows in grid layout
      };
    } else {
      return {
        titleFontMultiplier: 0.8,
        descriptionFontMultiplier: 0.45,
        gapMultiplier: 0.5,
        portraitTopOffset: 0.05,
        landscapeTopOffset: 0.1,
        imageSizeMultiplier: 2.5,
        columnWidthMultiplier: 0.42,
        portraitRowGapMultiplier: 1.0,
        gridSymbolSizeMultiplier: 1.2,
        paytableFontMultiplier: 0.5,
        rowGapMultiplier: 0.1,
      };
    }
  } else if (device === PixiSettings.DEVICE.TABLET) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 1.0,
        descriptionFontMultiplier: 0.55,
        gapMultiplier: 0.1,
        portraitTopOffset: 0.05,
        landscapeTopOffset: 0.0,
        imageSizeMultiplier: 2.2,
        columnWidthMultiplier: 0.9,
        portraitRowGapMultiplier: 1.0,
        gridSymbolSizeMultiplier: 1.5,
        paytableFontMultiplier: 0.5,
        rowGapMultiplier: 0.1,
      };
    } else {
      return {
        titleFontMultiplier: 1.4,
        descriptionFontMultiplier: 0.45,
        gapMultiplier: 0.5,
        portraitTopOffset: 0.05,
        landscapeTopOffset: 0.125,
        imageSizeMultiplier: 4.5,
        columnWidthMultiplier: 0.42,
        portraitRowGapMultiplier: 1.0,
        gridSymbolSizeMultiplier: 1.2,
        paytableFontMultiplier: 0.5,
        rowGapMultiplier: 0.01,
      };
    }
  } else {
    // Desktop
    return {
      titleFontMultiplier: 0.55,
      descriptionFontMultiplier: 0.45,
      gapMultiplier: 0.5,
      portraitTopOffset: 0.05,
      landscapeTopOffset: 0.15,
      imageSizeMultiplier: 2.5,
      columnWidthMultiplier: 0.42,
      portraitRowGapMultiplier: 1.0,
      gridSymbolSizeMultiplier: 1.2,
      paytableFontMultiplier: 0.8,
      rowGapMultiplier: 0.1,
    };
  }
};

/**
 * Paylines slide configuration
 * Controls image sizing and positioning for payline diagrams
 */
export const getPaylinesSlideImageConfig = (device, orientation) => {
  if (device === PixiSettings.DEVICE.MOBILE) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        imageWidthMultiplier: 0.8, // % of maxWidth
        imageHeightMultiplier: 0.8, // % of available height
        offsetX: 0,
        offsetY: -0.05,
      };
    } else {
      return {
        imageWidthMultiplier: 0.95,
        imageHeightMultiplier: 0.75,
        offsetX: 0,
        offsetY: -0.05,
      };
    }
  } else if (device === PixiSettings.DEVICE.TABLET) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        imageWidthMultiplier: 0.9,
        imageHeightMultiplier: 0.9,
        offsetX: 0,
        offsetY: 0,
      };
    } else {
      return {
        imageWidthMultiplier: 0.8,
        imageHeightMultiplier: 0.8,
        offsetX: 0,
        offsetY: -0.05,
      };
    }
  } else {
    // Desktop
    return {
      imageWidthMultiplier: 0.95,
      imageHeightMultiplier: 0.7,
      offsetX: 0,
      offsetY: 0,
    };
  }
};

/**
 * Feature 1 slide configuration (slideIndex 4)
 * Controls text and image layout for first feature
 */
export const getFeature1SlideTextConfig = (device, orientation) => {
  if (device === PixiSettings.DEVICE.MOBILE) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 0.75,
        descriptionFontMultiplier: 0.65,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.1,
        landscapeTopOffset: 0.0, // Offset for both text and image
        landscapeTextOffset: 0.0, // Additional offset for text only
        columnWidthMultiplier: 0.75, // % of maxWidth for text content
      };
    } else {
      return {
        titleFontMultiplier: 0.65,
        descriptionFontMultiplier: 0.55,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: -0.1,
        landscapeTextOffset: 0.2,
        columnWidthMultiplier: 0.42,
      };
    }
  } else if (device === PixiSettings.DEVICE.TABLET) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 0.8,
        descriptionFontMultiplier: 0.7,
        gapMultiplier: 0.5,
        portraitTopOffset: 0.05,
        landscapeTopOffset: 0.0,
        landscapeTextOffset: 0.0,
        columnWidthMultiplier: 0.75,
      };
    } else {
      return {
        titleFontMultiplier: 0.8,
        descriptionFontMultiplier: 0.7,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: -0.05,
        landscapeTextOffset: 0.1,
        columnWidthMultiplier: 0.42,
      };
    }
  } else {
    // Desktop
    return {
      titleFontMultiplier: 0.65,
      descriptionFontMultiplier: 0.45,
      gapMultiplier: 0.75,
      portraitTopOffset: 0.05,
      landscapeTopOffset: 0,
      landscapeTextOffset: 0.125,
      columnWidthMultiplier: 0.42,
    };
  }
};

/**
 * Feature 2 slide configuration (slideIndex 5)
 * Controls text and image layout for second feature
 */
export const getFeature2SlideTextConfig = (device, orientation) => {
  if (device === PixiSettings.DEVICE.MOBILE) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 0.75,
        descriptionFontMultiplier: 0.65,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.01,
        landscapeTopOffset: 0.0,
        landscapeTextOffset: 0.0,
        columnWidthMultiplier: 0.75,
      };
    } else {
      return {
        titleFontMultiplier: 0.65,
        descriptionFontMultiplier: 0.55,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: -0.1,
        landscapeTextOffset: 0.0,
        columnWidthMultiplier: 0.42,
      };
    }
  } else if (device === PixiSettings.DEVICE.TABLET) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 0.8,
        descriptionFontMultiplier: 0.7,
        gapMultiplier: 0.5,
        portraitTopOffset: 0.05,
        landscapeTopOffset: 0.0,
        landscapeTextOffset: 0.0,
        columnWidthMultiplier: 0.75,
      };
    } else {
      return {
        titleFontMultiplier: 0.8,
        descriptionFontMultiplier: 0.7,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: -0.05,
        landscapeTextOffset: 0.0,
        columnWidthMultiplier: 0.42,
      };
    }
  } else {
    // Desktop
    return {
      titleFontMultiplier: 0.55,
      descriptionFontMultiplier: 0.45,
      gapMultiplier: 1.0,
      portraitTopOffset: 0.05,
      landscapeTopOffset: 0.0,
      landscapeTextOffset: 0.0,
      columnWidthMultiplier: 0.42,
    };
  }
};

/**
 * Feature 3 slide configuration (slideIndex 6)
 * Controls text and image layout for third feature
 */
export const getFeature3SlideTextConfig = (device, orientation) => {
  if (device === PixiSettings.DEVICE.MOBILE) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 0.75,
        descriptionFontMultiplier: 0.65,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: 0.0,
        landscapeTextOffset: 0.0,
        columnWidthMultiplier: 0.75,
      };
    } else {
      return {
        titleFontMultiplier: 0.65,
        descriptionFontMultiplier: 0.55,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: -0.1, // Changed from -0.2 to 0.1 to move content down
        landscapeTextOffset: 0.1,
        columnWidthMultiplier: 0.42,
      };
    }
  } else if (device === PixiSettings.DEVICE.TABLET) {
    if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
      return {
        titleFontMultiplier: 0.8,
        descriptionFontMultiplier: 0.7,
        gapMultiplier: 0.5,
        portraitTopOffset: 0.05,
        landscapeTopOffset: 0.0,
        landscapeTextOffset: 0.0,
        columnWidthMultiplier: 0.75,
      };
    } else {
      return {
        titleFontMultiplier: 0.8,
        descriptionFontMultiplier: 0.7,
        gapMultiplier: 1.0,
        portraitTopOffset: 0.05,
        landscapeTopOffset: -0.05,
        landscapeTextOffset: 0.0,
        columnWidthMultiplier: 0.42,
      };
    }
  } else {
    // Desktop
    return {
      titleFontMultiplier: 0.55,
      descriptionFontMultiplier: 0.45,
      gapMultiplier: 1.0,
      portraitTopOffset: 0.05,
      landscapeTopOffset: 0.0,
      landscapeTextOffset: 0.0,
      columnWidthMultiplier: 0.42,
    };
  }
};
